import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { UnsavedChangesConfirmationDialog } from '../components/UnsavedChangesConfirmationDialog/UnsavedChangesConfirmationDialog';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ACTION_TYPES = {
  TAB_SWITCH: 'tab-switch',
  CLOSE: 'close'
};
const UnsavedChangesContext = /*#__PURE__*/createContext(undefined);
export function UnsavedChangesProvider({
  children
}) {
  const [pendingAction, setPendingAction] = useState(null);
  const [actionType, setActionType] = useState(ACTION_TYPES.TAB_SWITCH);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const unsavedChangesCheckRef = useRef(null);
  const onSaveRef = useRef(null);
  const onDiscardRef = useRef(null);
  const registerUnsavedChangesCheck = useCallback((checkFn, callbacks) => {
    var _callbacks$onSave, _callbacks$onDiscard, _callbacks$loading, _callbacks$failed;
    unsavedChangesCheckRef.current = checkFn;
    onSaveRef.current = (_callbacks$onSave = callbacks.onSave) !== null && _callbacks$onSave !== void 0 ? _callbacks$onSave : null;
    onDiscardRef.current = (_callbacks$onDiscard = callbacks.onDiscard) !== null && _callbacks$onDiscard !== void 0 ? _callbacks$onDiscard : null;
    setLoading((_callbacks$loading = callbacks.loading) !== null && _callbacks$loading !== void 0 ? _callbacks$loading : false);
    setFailed((_callbacks$failed = callbacks.failed) !== null && _callbacks$failed !== void 0 ? _callbacks$failed : false);
  }, []);
  const unregisterUnsavedChangesCheck = useCallback(() => {
    unsavedChangesCheckRef.current = null;
    onSaveRef.current = null;
    onDiscardRef.current = null;
    setLoading(false);
    setFailed(false);
  }, []);
  const hasUnsavedChanges = useCallback(() => {
    var _unsavedChangesCheckR, _unsavedChangesCheckR2;
    return (_unsavedChangesCheckR = (_unsavedChangesCheckR2 = unsavedChangesCheckRef.current) === null || _unsavedChangesCheckR2 === void 0 ? void 0 : _unsavedChangesCheckR2.call(unsavedChangesCheckRef)) !== null && _unsavedChangesCheckR !== void 0 ? _unsavedChangesCheckR : false;
  }, []);
  const requestAction = useCallback((action, type = ACTION_TYPES.TAB_SWITCH) => {
    if (hasUnsavedChanges()) {
      setPendingAction(() => action);
      setActionType(type);
      setShowConfirmation(true);
    } else {
      action();
    }
  }, [hasUnsavedChanges]);
  const handleLoadingFinish = useCallback(() => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setShowConfirmation(false);
  }, [pendingAction]);
  const handleConfirm = useCallback(async () => {
    if (onSaveRef.current) {
      await onSaveRef.current();
    }
  }, []);
  const handleCancel = useCallback(() => {
    var _onDiscardRef$current;
    (_onDiscardRef$current = onDiscardRef.current) === null || _onDiscardRef$current === void 0 || _onDiscardRef$current.call(onDiscardRef);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setShowConfirmation(false);
  }, [pendingAction]);
  const handleClose = useCallback(() => {
    setShowConfirmation(false);
    setPendingAction(null);
  }, []);
  return /*#__PURE__*/_jsxs(UnsavedChangesContext.Provider, {
    value: {
      registerUnsavedChangesCheck,
      unregisterUnsavedChangesCheck,
      requestAction,
      hasUnsavedChanges,
      isDialogShowing: showConfirmation
    },
    children: [children, showConfirmation && /*#__PURE__*/_jsx(UnsavedChangesConfirmationDialog, {
      actionType: actionType,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
      onClose: handleClose,
      onLoadingFinish: handleLoadingFinish,
      loading: loading,
      failed: failed
    })]
  });
}
export function useUnsavedChanges() {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChanges must be used within UnsavedChangesProvider');
  }
  return context;
}
export function useRegisterUnsavedChanges(hasUnsavedChangesFn, callbacks) {
  const {
    registerUnsavedChangesCheck,
    unregisterUnsavedChangesCheck
  } = useUnsavedChanges();
  const {
    onSave,
    onDiscard,
    loading,
    failed
  } = callbacks;
  useEffect(() => {
    registerUnsavedChangesCheck(hasUnsavedChangesFn, {
      onSave,
      onDiscard,
      loading,
      failed
    });
    return () => {
      unregisterUnsavedChangesCheck();
    };
  }, [registerUnsavedChangesCheck, unregisterUnsavedChangesCheck, hasUnsavedChangesFn, onSave, onDiscard, loading, failed]);
}